import { EquipUIFireflyV2Theme } from '@equip.health/ui';
import * as CronofyElements from 'cronofy-elements';

import {
  VITE_CRONOFY_CLIENT_ID,
  VITE_MAUD_BASE_URL,
} from '~/lib/constants/env';
import { Path } from '~/routes/constants';

const { neutral, primary } = EquipUIFireflyV2Theme.colors;

export enum CalendarResource {
  AVAILABILITY_RULES = 'Availability Rules',
  CALENDAR_SYNC = 'Sync Calendar',
  DATE_TIME_PICKER = 'Date Time Picker',
  DATE_TIME_PICKER_V2 = 'Date Time Picker V2',
  RESCHEDULE_APPOINTMENT_DATE_TIME_PICKER = 'Reschedule Appointment Date Time Picker',
}

export const CalendarResourceMapper: Record<
  CalendarResource,
  CalendarResourceConfig
> = {
  [CalendarResource.AVAILABILITY_RULES]: {
    element: CronofyElements.AvailabilityRules,
    options: {
      callback: (data: CalendarEvent) =>
        console.log('availability rules notification', data),
      styles: {
        colors: {
          available: 'white',
          unavailable: 'white',
        },
        prefix: 'availability-rules',
      },
      target_id: 'cronofy-availability-rules',
    },
  },
  [CalendarResource.CALENDAR_SYNC]: {
    element: CronofyElements.CalendarSync,
    options: {
      authorization: {
        client_id: VITE_CRONOFY_CLIENT_ID,
        redirect_uri: `${VITE_MAUD_BASE_URL}${Path.CALENDAR_INTEGRATION}`,
        scope: 'read_write',
      },
      callback: (data: CalendarEvent) =>
        console.log('calendar sync notification', data),
      single_profile: true,
      styles: {
        prefix: 'calendar-sync',
      },
      target_id: 'cronofy-element-calendar-sync',
    },
  },
  [CalendarResource.DATE_TIME_PICKER]: {
    element: CronofyElements.DateTimePicker,
    options: {
      callback: (data: CalendarEvent) =>
        console.log('date time picker notification', data),
      config: {
        mode: 'no_confirm',
      },
      styles: {
        colors: {
          button: 'transparent',
          buttonActive: primary[100],
          buttonActiveText: neutral.background.primary,
          buttonHover: neutral.background.secondary,
          buttonHoverText: primary[100],
        },
        prefix: 'scheduler',
      },
      target_id: 'cronofy-date-time-picker',
    },
  },
  [CalendarResource.DATE_TIME_PICKER_V2]: {
    element: CronofyElements.DateTimePicker,
    options: {
      config: {
        display_slot_timezone: false,
        mode: 'no_confirm',
      },
      styles: {
        colors: {
          button: 'transparent',
          buttonActive: primary[100],
          buttonActiveText: neutral.background.primary,
          buttonHover: primary[10],
          buttonHoverText: primary[100],
        },
        prefix: 'scheduler2',
      },
      target_id: 'cronofy-date-time-picker-v2',
    },
  },
  [CalendarResource.RESCHEDULE_APPOINTMENT_DATE_TIME_PICKER]: {
    element: CronofyElements.DateTimePicker,
    options: {
      callback: () => {},
      config: {
        display_slot_timezone: true,
        mode: 'no_confirm',
      },
      styles: {
        colors: {
          button: 'transparent',
          buttonActive: primary[100],
          buttonActiveText: neutral.background.primary,
          buttonHover: primary[10],
          buttonHoverText: primary[100],
        },
        prefix: 'scheduler2',
      },
      target_id: 'reschedule-cronofy-date-time-picker',
    },
  },
};

export const CalendarResourceTokenTypeMapper: Record<CalendarResource, string> =
  {
    [CalendarResource.AVAILABILITY_RULES]: 'manage-availability',
    [CalendarResource.CALENDAR_SYNC]: 'manage-account',
    [CalendarResource.DATE_TIME_PICKER]: 'view-availability',
    [CalendarResource.DATE_TIME_PICKER_V2]: 'view-availability',
    [CalendarResource.RESCHEDULE_APPOINTMENT_DATE_TIME_PICKER]:
      'view-availability',
  };

/** Cronofy availability API allows up to 50 query periods: https://docs.cronofy.com/developers/api/scheduling/availability/#param-query_periods */
export const MAX_QUERY_PERIODS = 50;

export const UNKNOWN_AVAILABILITY_RULE_ERROR =
  'errors.availability_rule_id_unknown';

export const FETCH_APPOINTMENT_TYPES_ERROR =
  'Error retrieving appointment types';

export const FETCH_APPOINTMENTS_ERROR = 'Error retrieving appointment data';

export enum SchedulerStep {
  SELECT_SLOT,
  REVIEW,
}

export const WEEK_VIEW_TIMES: string[] = [
  '12 AM',
  '1 AM',
  '2 AM',
  '3 AM',
  '4 AM',
  '5 AM',
  '6 AM',
  '7 AM',
  '8 AM',
  '9 AM',
  '10 AM',
  '11 AM',
  '12 PM',
  '1 PM',
  '2 PM',
  '3 PM',
  '4 PM',
  '5 PM',
  '6 PM',
  '7 PM',
  '8 PM',
  '9 PM',
  '10 PM',
  '11 PM',
];

export const WEEK_VIEW_ITEM_HEIGHT = 24;
export const WEEKDAYS_ALTERNATIVE: number[] = [...Array(7).keys()];
export const OVERVIEW_TAB_APPOINTMENTS_COUNT = 3;

export enum RecurringAppointmentUpdateOption {
  ONLY = 'Only this appointment',
  ALL = 'All future appointments in this series',
}

export enum AppointmentStatus {
  CANCELLED = 'cancelled',
  COMPLETED = 'completed',
  SCHEDULED = 'scheduled',
}
export enum AppointmentCancelReasonOption {
  PATIENT_CANCELLED = 'Patient cancelled',
  PATIENT_NO_SHOW = 'Patient no show',
  PROVIDER_CANCELLED = 'Provider cancelled',
  APPOINTMENT_FREQUENCY_OR_DURATION_CHANGES = 'Appointment frequency or duration changes',
  PATIENT_DISCHARGED = 'Patient discharged',
  APPOINTMENT_MADE_IN_ERROR = 'Appointment made in error',
}

export const CANCEL_APPOINTMENT_API_ERROR_TEXT =
  'Unable to cancel the appointment, please try again';
export const OPEN_ZOOM_API_ERROR_TEXT =
  'Failed to join Zoom meeting. Please try again.';
export const ZOOM_USER_DOES_NOT_EXIST_ERROR_TEXT = "Zoom user doesn't exist.";
export const NO_PATIENT_OR_SUPPORT_ATTENDEE_WARNING =
  'At least one patient or support must be selected in order to schedule an appointment.';

export const MINIMUM_PROVIDER_ATTENDEES_WARNING =
  'At least two providers must be selected to schedule this appointment.';
export const CALENDAR_NOT_INTEGRATED_ERROR =
  'In order to be included in an appointment, we require an active Maud calendar integration. Please go to the Integration tab on the Calendar page to proceed.';
export const GROUP_CLASS_TITLE_LIMIT = 200;
export const GROUP_CLASS_DESCRIPTION_LIMIT = 500;
export const GROUP_CLASS_DURATIONS = [30, 60, 90, 120];

export const REGISTRATION_CRITERIA = 'Registration criteria';
export const ALL_CLASSES_REQUIRED_V2 = 'All classes required';
export const REGISTRATION_ENDS = 'Registration ends after first class';
export const SENSITIVE_GROUP_CLASS_LABEL =
  'Mark as sensitive (hide registration status from proxy supports)';
export const GROUP_CLASS_CREATE_ERROR_TEXT =
  "We're sorry, we are unable to create a class at this time. Please try again.";

export const GROUP_CLASS_EDIT_ERROR_TEXT =
  "We're sorry, we are unable to edit the class at this time. Please try again.";
export const CANCELLATION_APPOINTMENT_REASON =
  'Why are you cancelling this appointment?';
export const CANCELLATION_APPOINTMENT_SUB_REASON = 'Select additional context';

export const CANCELLATION_SCOPE_LABEL = 'What would you like to cancel?';
export const EDIT_SCOPE_LABEL = 'What would you like to edit?';
export const WEEKLY_AVAILABILITY_LABEL = 'Weekly availability';
export const EDIT_AVAILABILITY_LABEL = 'Edit availability';
export const AVAILABILITY_EDIT_TEXT =
  "You've made changes to this user's availability. Please save these changes in order for them to take effect.";
